import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { getDepartmentAsHeadOfOptions } from '@/module/Department/model/DepartmentQuery';
import { Department } from '@/module/Department/model/DepartmentTypes';
import { getDisciplineAsHeadOfOptions, getDisciplineUsersOptions } from '@/module/Discipline/model/DisciplineQuery';
import { Discipline } from '@/module/Discipline/model/DisciplineTypes';
import { getFacultiesAsHeadOfOptions, getSuspenseFacultyOptions } from '@/module/Faculty/model/FacultyQuery';
import { Faculty } from '@/module/Faculty/model/FacultyTypes';

function useSearchState(key: string) {
  const navigate = useNavigate();
  const value: string | null = useSearch({
    strict: false,
    // @ts-expect-error refactor
    select: sr => sr[key],
  });

  const setValue = (newValue: string | null) => {
    navigate({
      to: '.',
      search: prev => ({ ...prev, [key]: newValue }),
    });
  };

  return [value != null ? String(value) : null, setValue] as const;
}

function useDisciplineHierarchy(disciplines: Discipline[]) {
  const [disciplineValue, setDisciplineValue] = useSearchState('disciplineId');
  const [userValue, setUserValue] = useSearchState('userId');

  const disciplineOptions = disciplines.map(dsc => ({ value: String(dsc.id), label: dsc.name }));

  const { data: users } = useSuspenseQuery(getDisciplineUsersOptions(disciplineValue ?? undefined));
  const userOptions = users.map(dsc => ({ value: String(dsc.id), label: dsc.name }));

  return {
    disciplineId: disciplineValue ? Number(disciplineValue) : null,
    userId: userValue ? Number(userValue) : null,

    selectProps: {
      disciplineOptions,
      userOptions,

      disciplineValue,
      userValue,

      setUserValue,
      setDisciplineValue,

      onDisciplineChange: (_value: string | null) => {
        setDisciplineValue(_value);
        setUserValue(null);
      },

      onUserChange: (_value: string | null) => {
        setUserValue(_value);
      },
    },
  };
}

function useDepartmentHierarchy(departments: Department[]) {
  const [departmentValue, setDepartmentValue] = useSearchState('departmentId');
  const departmentOptions = departments.map(dsc => ({ value: String(dsc.id), label: dsc.name }));

  const selectedDepartment = departments.filter(dep => String(dep.id) === departmentValue)[0];

  const { disciplineId, userId, selectProps } = useDisciplineHierarchy(selectedDepartment?.disciplines ?? []);

  return {
    userId,
    disciplineId,
    departmentId: departmentValue ? Number(departmentValue) : null,

    selectProps: {
      ...selectProps,

      departmentValue,
      departmentOptions,
      setDepartmentValue,

      onDepartmentChange: (_value: string | null) => {
        setDepartmentValue(_value);
        selectProps.setDisciplineValue(null);
        selectProps.setUserValue(null);
      },
    },
  };
}

function useAdminHierarchy(faculties: Faculty[]) {
  const [facultyValue, setFacultyValue] = useSearchState('facultyId');

  const facultyOptions = faculties.map(f => ({ value: String(f.id), label: f.name }));
  const selectedFaculty = faculties.filter(f => String(f.id) === facultyValue)[0];

  const { disciplineId, departmentId, userId, selectProps } = useDepartmentHierarchy(
    selectedFaculty?.departments ?? [],
  );

  return {
    disciplineId,
    departmentId,
    userId,
    facultyId: facultyValue ? Number(facultyValue) : null,

    selectProps: {
      ...selectProps,

      facultyOptions,
      setFacultyValue,
      facultyValue,

      onFacultyChange: (_value: string | null) => {
        setFacultyValue(_value);
        selectProps.setDepartmentValue(null);
        selectProps.setDisciplineValue(null);
        selectProps.setUserValue(null);
      },
    },
  };
}

export function useUniversityAdminHierarchy() {
  const { data: faculties } = useSuspenseQuery(getSuspenseFacultyOptions());
  return useAdminHierarchy(faculties);
}

export function useFacultyAdminHierarchy() {
  const { data: faculties } = useSuspenseQuery(getFacultiesAsHeadOfOptions());
  return useAdminHierarchy(faculties);
}

export function useDisciplineAdminHierarchy() {
  const { data: disciplines } = useSuspenseQuery(getDisciplineAsHeadOfOptions());
  return useDisciplineHierarchy(disciplines);
}

export function useDepartmentAdminHierarchy() {
  const { data: departments } = useSuspenseQuery(getDepartmentAsHeadOfOptions());
  return useDepartmentHierarchy(departments);
}
